import { Link, router } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import { PlusIcon, MessageSquareIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { FDEConversation } from '@/types/fde'

interface FDEConversationListProps {
  conversations: FDEConversation[]
  activeConversationId: number | null
}

export function FDEConversationList({ conversations, activeConversationId }: FDEConversationListProps) {
  const { t } = useTranslation('fde')

  const grouped = groupByDate(conversations, {
    today: t('conversationList.today'),
    yesterday: t('conversationList.yesterday'),
    thisWeek: t('conversationList.thisWeek'),
    earlier: t('conversationList.earlier'),
  })

  const handleNewConversation = () => {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
    router.post('/fde', {}, {
      headers: { 'X-CSRF-Token': csrfToken },
    })
  }

  return (
    <div className="flex h-full w-72 shrink-0 flex-col overflow-hidden border-r">
      <div className="flex shrink-0 items-center justify-between border-b px-4 py-3">
        <h2 className="text-sm font-semibold">{t('conversationList.title')}</h2>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleNewConversation}>
          <PlusIcon className="h-4 w-4" />
          <span className="sr-only">{t('conversationList.newConversation')}</span>
        </Button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        {conversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-6 text-center">
            <MessageSquareIcon className="h-8 w-8 text-muted-foreground/50 mb-2" />
            <p className="text-sm text-muted-foreground">{t('conversationList.empty')}</p>
          </div>
        ) : (
          <div className="p-2 space-y-4">
            {grouped.map(group => (
              <div key={group.label}>
                <p className="px-2 py-1.5 text-xs font-medium text-muted-foreground">{group.label}</p>
                <div className="space-y-1">
                  {group.conversations.map(conv => (
                    <Link
                      key={conv.id}
                      href={`/fde/${conv.id}`}
                      className={cn(
                        "block truncate rounded-md px-2 py-2 text-sm transition-colors hover:bg-accent",
                        conv.id === activeConversationId && "bg-accent font-medium"
                      )}
                    >
                      {conv.title || 'New Conversation'}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

interface DateGroup {
  label: string
  conversations: FDEConversation[]
}

function groupByDate(conversations: FDEConversation[], labels: Record<string, string>): DateGroup[] {
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const yesterday = new Date(today.getTime() - 86400000)
  const weekAgo = new Date(today.getTime() - 7 * 86400000)

  const groups: Record<string, FDEConversation[]> = {
    today: [],
    yesterday: [],
    thisWeek: [],
    earlier: [],
  }

  conversations.forEach(conv => {
    const date = new Date(conv.updated_at)
    if (date >= today) groups.today.push(conv)
    else if (date >= yesterday) groups.yesterday.push(conv)
    else if (date >= weekAgo) groups.thisWeek.push(conv)
    else groups.earlier.push(conv)
  })

  const result: DateGroup[] = []
  if (groups.today.length) result.push({ label: labels.today, conversations: groups.today })
  if (groups.yesterday.length) result.push({ label: labels.yesterday, conversations: groups.yesterday })
  if (groups.thisWeek.length) result.push({ label: labels.thisWeek, conversations: groups.thisWeek })
  if (groups.earlier.length) result.push({ label: labels.earlier, conversations: groups.earlier })

  return result
}
